import React, { ReactNode } from 'react';
import { Disclosure, Transition } from '@headlessui/react';
import useClassNames from '@loomstateorg/shared-frontend/hooks/useClassNames';
import AccordionButton from './AccordionButton';

export interface AccordionProps {
  children?: React.ReactNode;

  index?: number;
  variant?: 'arrow' | 'plusAndMinus';
  accordionListLength?: number;
  className?: string;
  openSectionTitle?: string;
  closedSectionTitle?: string;
  iconClassName?: string;
  buttonClassName?: string;
  buttonWrapperClassName?: string;
  panelClassName?: string;
  collapsedLabel?: string;
  customOpenButton?: ReactNode;
  customClosedButton?: ReactNode;
  onClick?: () => void;
  defaultOpen?: boolean;
  ref?: React.RefObject<HTMLDivElement>;
  /** Replaces `atom-accordion` on the container; the toggle becomes `<data-testid>-toggle`. */
  'data-testid'?: string;
}

const Accordion = (
  {
    variant = 'arrow',
    closedSectionTitle,
    openSectionTitle = closedSectionTitle,
    children,
    className = '',
    iconClassName = '',
    buttonClassName = '',
    buttonWrapperClassName = '',
    panelClassName = '',
    collapsedLabel,
    customClosedButton,
    customOpenButton = customClosedButton,
    defaultOpen = false,
    onClick,
    'data-testid': dataTestId,
  }: React.PropsWithChildren<AccordionProps>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const panelClassNames = useClassNames([panelClassName]);
  const toggleTestId = dataTestId ? `${dataTestId}-toggle` : 'atom-accordion-button';

  return (
    // testid-ignore: the onClick is the caller's callback; the Disclosure button below is the toggle
    <div className={className} onClick={onClick} ref={ref} data-testid={dataTestId ?? 'atom-accordion'}>
      <Disclosure defaultOpen={defaultOpen}>
        {({ open }) => (
          <>
            {(open && !customOpenButton) || (!open && !customClosedButton) ? (
              <AccordionButton
                open={open}
                variant={variant}
                collapsedLabel={collapsedLabel}
                buttonClassName={buttonClassName}
                iconClassName={iconClassName}
                openSectionTitle={openSectionTitle}
                closedSectionTitle={openSectionTitle}
                buttonWrapperClassName={buttonWrapperClassName}
                data-testid={toggleTestId}
              />
            ) : (
              <Disclosure.Button className={buttonClassName} data-testid={toggleTestId}>
                {open ? customOpenButton : customClosedButton}
              </Disclosure.Button>
            )}

            <Transition
              enter="transition duration-150 ease-out"
              enterFrom="transform scale-y-95 opacity-0"
              enterTo="transform scale-y-100 opacity-100"
              leave="transition duration-100 ease-out"
              leaveFrom="transform scale-y-100 opacity-100"
              leaveTo="transform scale-y-95 opacity-0"
              unmount={false}
            >
              <Disclosure.Panel className={panelClassNames} unmount={false}>
                {children}
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>
    </div>
  );
};

export default React.forwardRef<HTMLDivElement, React.PropsWithChildren<AccordionProps>>(Accordion);
